import { useRef, useState } from "react";

import { ChevronLeft, Eye, FileText, Trash2, UploadCloud } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useMedicalReportsActions } from "@/features/imagings/hooks/useMedicalReportsActions";
import { useReportSharing } from "@/features/imagings/hooks/useReportSharing";
import type { Imaging } from "@/features/imagings/schemas/imagings";
import type { Report } from "@/features/imagings/types/reports";

import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";

/**
 * Props para el componente ReportViewer
 */
interface ReportViewerProps {
  reports: Report[];
  isOpen: boolean;
  onClose: () => void;
  imaging?: Imaging;
  onShareReport?: (report: Report) => void;
  onDownloadReport?: (report: Report) => void;
  onRefresh?: () => void;
}

type ViewMode = "list" | "upload";

/**
 * Componente para visualizar informes médicos con soporte para carga y visualización de metadatos
 */
export function ReportViewer({
  reports,
  isOpen,
  onClose,
  imaging,
  onRefresh,
}: ReportViewerProps) {
  const { openReportInNewTab } = useReportSharing();
  const {
    uploadReport,
    deleteFile,
    deleteRIS,
    isDeletingFile,
    isDeletingRIS,
    isUploading,
  } = useMedicalReportsActions();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<Report | null>(null);

  // Upload Form State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [responsible, setResponsible] = useState("");
  const [dateReport, setDateReport] = useState(new Date().toISOString().split("T")[0]);
  const [description, setDescription] = useState("");

  const currentReport = reports && reports.length > 0 ? reports[0] : null;
  const rawPatientName =
    imaging?.patient_name || currentReport?.patient_name || "Paciente Desconocido";
  const patientName =
    typeof rawPatientName === "string"
      ? rawPatientName.replace(/\^/g, " ").trim()
      : "Paciente Desconocido";
  const modalityName =
    imaging?.modality || currentReport?.modality || "Modalidad Desconocida";

  /**
   * Formatea la fecha para mostrar de forma segura
   */
  const formatDate = (date: any) => {
    try {
      if (!date) return "—";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "—";
      return new Intl.DateTimeFormat("es-ES", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      }).format(d);
    } catch {
      return "—";
    }
  };

  /**
   * Abre el informe en una nueva pestaña
   */
  const openInNewTab = async (report: Report) => {
    try {
      await openReportInNewTab(report);
    } catch (error) {
      console.error("Error abriendo informe:", error);
    }
  };

  const handleDeleteClick = (report: Report) => {
    setReportToDelete(report);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      if (reportToDelete.type === "manual") {
        await deleteFile.mutateAsync({
          idFile: reportToDelete.id,
          idStudy: reportToDelete.imaging_id,
        });
      } else {
        await deleteRIS.mutateAsync(reportToDelete.id);
      }
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error en la eliminación:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setViewMode("upload");
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile || !imaging?.uid) return;

    try {
      await uploadReport.mutateAsync({
        file: selectedFile,
        id_study: imaging.uid,
        filename: selectedFile.name,
        responsible,
        date_report: dateReport,
        description,
      });
      // Reset form and go back to list
      setSelectedFile(null);
      setResponsible("");
      setDescription("");
      setViewMode("list");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error subiendo reporte:", error);
    }
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setViewMode("list");
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
            setViewMode("list");
            setSelectedFile(null);
          }
        }}
      >
        <DialogContent className="top-auto! bottom-0! left-0! translate-x-0! translate-y-0! w-full! max-w-none! rounded-b-none! rounded-t-3xl! sm:top-[50%]! sm:bottom-auto! sm:left-[50%]! sm:-translate-x-1/2! sm:-translate-y-1/2! sm:rounded-2xl! sm:w-[95vw]! sm:max-w-md! md:max-w-lg! max-h-[90dvh] sm:max-h-[85vh] p-0 overflow-hidden bg-white dark:bg-slate-950 border-none sm:shadow-2xl shadow-[0_-8px_30px_rgb(0,0,0,0.12)]">
          <DialogHeader className="p-4 sm:p-6 pb-4 flex flex-row items-center gap-3 sm:gap-4 space-y-0 relative border-b border-slate-100 dark:border-slate-800">
            {viewMode === "upload" && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={handleCancelUpload}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-xl ${viewMode === "upload" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : "bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400"} shrink-0`}
            >
              <FileText className="h-6 w-6" />
            </div>
            <div className="flex flex-col gap-0.5 text-left flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold m-0 p-0 text-slate-900 dark:text-slate-50">
                {viewMode === "list" ? "Informes Médicos" : "Registrar Informe"}
              </DialogTitle>
              <p className="text-xs text-muted-foreground truncate w-full uppercase tracking-wider font-medium">
                {modalityName} • {patientName}
              </p>
            </div>
          </DialogHeader>

          <div className="max-h-[75dvh] sm:max-h-[70dvh] overflow-y-auto custom-scrollbar touch-pan-y">
            {viewMode === "list" ? (
              <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
                {/* Upload New Report Area */}
                <div className="w-full">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept=".pdf"
                    onChange={handleFileChange}
                  />
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-teal-200 dark:border-teal-800/50 rounded-2xl bg-slate-50/50 dark:bg-slate-900/20 p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-all group"
                  >
                    <div className="h-12 w-12 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mb-4 text-teal-600 dark:text-teal-400 group-hover:scale-110 transition-transform">
                      <UploadCloud className="h-6 w-6" />
                    </div>
                    <h3 className="text-base font-bold text-foreground mb-1">
                      Subir Nuevo Informe
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-[200px] leading-relaxed">
                      Haz clic o arrastra un archivo PDF para adjuntar al estudio
                    </p>
                  </div>
                </div>

                {/* Available Reports List */}
                <div className="space-y-4">
                  <h3 className="font-bold text-sm text-foreground uppercase tracking-widest">
                    Lista de Informes ({reports.length})
                  </h3>

                  {reports.length === 0 ? (
                    <div className="text-center py-10 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                      <FileText className="h-8 w-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">
                        No hay informes adjuntos todavía
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reports.map((report, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-10 w-10 shrink-0 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400">
                              <FileText className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="font-bold text-sm text-foreground truncate">
                                {report.type === "ris"
                                  ? "Informe RIS"
                                  : "Archivo de Informe"}
                              </span>
                              <span className="text-[10px] text-muted-foreground uppercase font-medium mt-0.5">
                                {formatDate(report.created_at)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 dark:text-blue-400"
                              onClick={() => openInNewTab(report)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-900/20 dark:hover:bg-red-900/40 dark:text-red-400"
                              onClick={() => handleDeleteClick(report)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-5">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl flex items-center gap-3 border border-blue-100 dark:border-blue-900/30">
                  <div className="h-10 w-10 bg-white dark:bg-blue-800 rounded-lg flex items-center justify-center text-blue-600 dark:text-blue-200 shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate text-blue-900 dark:text-blue-100">
                      {selectedFile?.name}
                    </p>
                    <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium uppercase">
                      Archivo PDF seleccionado
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <div className="grid gap-2">
                    <Label
                      htmlFor="responsible"
                      className="text-xs font-bold uppercase tracking-wider text-slate-500"
                    >
                      Responsable / Firma
                    </Label>
                    <Input
                      id="responsible"
                      placeholder="Ej: Dr. Juan Pérez"
                      value={responsible}
                      onChange={(e) => setResponsible(e.target.value)}
                      className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label
                      htmlFor="date"
                      className="text-xs font-bold uppercase tracking-wider text-slate-500"
                    >
                      Fecha del Informe
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      value={dateReport}
                      onChange={(e) => setDateReport(e.target.value)}
                      className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label
                      htmlFor="description"
                      className="text-xs font-bold uppercase tracking-wider text-slate-500"
                    >
                      Descripción (Opcional)
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Nota breve sobre el informe..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="min-h-[100px] bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 resize-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="p-4 sm:p-6 pt-3 sm:pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2 sm:gap-3 bg-white dark:bg-slate-950">
            {viewMode === "list" ? (
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full max-w-[120px] font-bold text-xs uppercase tracking-widest h-10"
              >
                Cerrar
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleCancelUpload}
                  variant="ghost"
                  disabled={isUploading}
                  className="font-bold text-xs uppercase tracking-widest h-10"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleUploadSubmit}
                  disabled={isUploading || !responsible || !dateReport}
                  className="min-w-[140px] font-bold text-xs uppercase tracking-widest h-10 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {isUploading ? "Registrando..." : "Guardar Registro"}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeletingFile || isDeletingRIS}
        title={reportToDelete?.type === "manual" ? "Eliminar Archivo" : "Limpiar Informe"}
        description={
          reportToDelete?.type === "manual"
            ? "¿Estás seguro de que deseas eliminar este archivo? Esta acción es irreversible y se eliminará permanentemente de Firebase Storage y Firestore."
            : "¡Atención! Estás a punto de anular el informe médico. Se borrará el PDF físico y se marcará el registro como vacío. ¿Deseas continuar?"
        }
        confirmText={
          reportToDelete?.type === "manual"
            ? "Eliminar Permanentemente"
            : "Confirmar Limpieza"
        }
        cancelText={reportToDelete?.type === "manual" ? "Cancelar" : "Mantener Informe"}
        variant={reportToDelete?.type === "manual" ? "danger" : "warning"}
      />
    </>
  );
}
